import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { useAccounts } from '../../../context/AccountContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import {
  LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { RefreshCw, Loader2, AlertCircle, Users, Repeat } from 'lucide-react';
import { isDemoBrand, demoHash } from '../../../utils/demoBrand';

const API_URL = 'https://api.glowytics.com';

// Static monthly Remarketing-vs-New-User dataset for demo brands. Returns the same
// { rows, accountId, window } shape the live endpoint does; the page classifies rows
// by the "RM" keyword, so half the campaigns carry it and half don't.
function buildDemoRmVsNew() {
  const rmCampaigns = ['RM | 30D Site', 'RM | Cart Abandoners', 'RM | 180D Purchasers'];
  const nuCampaigns = ['ADV+ Prospecting', 'Lookalike 3% | Purchase', 'Broad | Interest Stack', 'Reels | New User'];
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  const rows = [];
  const mkRow = (name, month, isRm) => {
    const h = demoHash(`${name}-${month}`);
    // New-user campaigns spend more but convert at a lower ROAS than remarketing.
    const spend = (isRm ? 18000 : 42000) + (h % (isRm ? 12000 : 30000));
    const roas = (isRm ? 4.1 : 2.4) + (h % 90) / 100;
    const revenue = Math.round(spend * roas);
    const impressions = spend * (isRm ? 22 : 34) + (h % 5000);
    const clicks = Math.round(impressions * (0.009 + (h % 12) / 1000));
    const reach = Math.round(impressions / (1.6 + (h % 8) / 10));
    const conversions = Math.round(clicks * (isRm ? 0.06 : 0.035) + (h % 20));
    rows.push({ campaign_name: name, month, spend, impressions, clicks, reach, conversions, revenue });
  };
  months.forEach((m) => {
    rmCampaigns.forEach((n) => mkRow(n, m, true));
    nuCampaigns.forEach((n) => mkRow(n, m, false));
  });
  return {
    rows,
    accountId: 'act_demo',
    window: { since: `${months[0]}-01`, until: `${months[months.length - 1]}-28` },
  };
}
const RM_COLOR = '#7c3aed';   // Remarketing — violet
const NU_COLOR = '#059669';   // New User — emerald

const DATE_RANGES = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'last180', label: 'Last 180 days' },
  { value: 'thisYear', label: 'This year' },
  { value: 'all', label: 'All time' },
];

const fmtInt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtMoney = (n) => '₺' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => fmtNum(n, 2) + '%';
const roasStr = (n) => fmtNum(n, 2) + 'x';

const METRICS = [
  { key: 'spend', label: 'Spend', fmt: fmtMoney, higher: false },
  { key: 'revenue', label: 'Revenue', fmt: fmtMoney, higher: true },
  { key: 'roas', label: 'ROAS', fmt: roasStr, higher: true },
  { key: 'conversions', label: 'Conversions', fmt: fmtInt, higher: true },
  { key: 'cpa', label: 'CPA', fmt: fmtMoney, higher: false },
  { key: 'ctr', label: 'CTR', fmt: fmtPct, higher: true },
  { key: 'cpc', label: 'CPC', fmt: fmtMoney, higher: false },
  { key: 'cpm', label: 'CPM', fmt: fmtMoney, higher: false },
  { key: 'impressions', label: 'Impressions', fmt: fmtInt, higher: true },
  { key: 'clicks', label: 'Clicks', fmt: fmtInt, higher: true },
  { key: 'reach', label: 'Reach', fmt: fmtInt, higher: true },
];

const CHART_METRICS = [
  { key: 'spend', label: 'Spend', val: (s) => s.spend, fmt: fmtMoney },
  { key: 'revenue', label: 'Revenue', val: (s) => s.revenue, fmt: fmtMoney },
  { key: 'roas', label: 'ROAS', val: (s) => (s.spend > 0 ? s.revenue / s.spend : 0), fmt: roasStr },
  { key: 'conversions', label: 'Conversions', val: (s) => s.conversions, fmt: fmtInt },
  { key: 'impressions', label: 'Impressions', val: (s) => s.impressions, fmt: fmtInt },
];

const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const blank = () => ({ spend: 0, impressions: 0, clicks: 0, reach: 0, conversions: 0, revenue: 0 });
const add = (t, r) => {
  t.spend += Number(r.spend || 0); t.impressions += Number(r.impressions || 0); t.clicks += Number(r.clicks || 0);
  t.reach += Number(r.reach || 0); t.conversions += Number(r.conversions || 0); t.revenue += Number(r.revenue || 0);
};
const derive = (t) => ({
  ...t,
  ctr: t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0,
  cpc: t.clicks > 0 ? t.spend / t.clicks : 0,
  cpm: t.impressions > 0 ? (t.spend / t.impressions) * 1000 : 0,
  cpa: t.conversions > 0 ? t.spend / t.conversions : 0,
  roas: t.spend > 0 ? t.revenue / t.spend : 0,
});

export default function MetaRmVsNewUser() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const accParam = selectedAccountId && selectedAccountId !== 'all' ? selectedAccountId : undefined;

  const [dateRange, setDateRange] = useState('last90');
  const [keyword, setKeyword] = useState('RM');
  const [chartMetric, setChartMetric] = useState('spend');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resp, setResp] = useState(null);

  const load = useCallback(async () => {
    if (!selectedBrand?.id) { setError('Select a brand first.'); return; }
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/meta-ads/live/campaign-monthly`, {
        params: { dateRange, ...(accParam ? { accountId: accParam } : {}) },
        headers: { 'X-Brand-ID': selectedBrand.id },
        withCredentials: true, timeout: 120000,
      });
      const rows = r.data?.rows || [];
      setResp((isDemoBrand(selectedBrand) && rows.length === 0) ? buildDemoRmVsNew() : r.data);
    } catch (e) {
      if (isDemoBrand(selectedBrand)) { setResp(buildDemoRmVsNew()); setError(null); }
      else setError(e?.response?.data?.error || e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, dateRange, accParam]);

  useEffect(() => { load(); }, [load]);

  // Classify each row's campaign as Remarketing (name token = keyword) vs New User
  const isRM = useMemo(() => {
    const k = escRe(keyword.trim());
    const re = k ? new RegExp(`(^|[^a-z0-9])${k}([^a-z0-9]|$)`, 'i') : null;
    return (name) => (re && re.test(String(name || ''))) || /remarket/i.test(String(name || ''));
  }, [keyword]);

  const model = useMemo(() => {
    const rows = resp?.rows || [];
    const rmTot = blank(), nuTot = blank();
    const monthMap = {}; // month -> { rm, nu }
    const camps = { rm: new Set(), nu: new Set() };
    for (const r of rows) {
      const seg = isRM(r.campaign_name) ? 'rm' : 'nu';
      add(seg === 'rm' ? rmTot : nuTot, r);
      camps[seg].add(r.campaign_name);
      const m = r.month || '?';
      if (!monthMap[m]) monthMap[m] = { rm: blank(), nu: blank() };
      add(monthMap[m][seg], r);
    }
    const months = Object.keys(monthMap).sort();
    return {
      rm: derive(rmTot), nu: derive(nuTot),
      rmCount: camps.rm.size, nuCount: camps.nu.size,
      months, monthMap,
    };
  }, [resp, isRM]);

  const cm = CHART_METRICS.find((m) => m.key === chartMetric) || CHART_METRICS[0];
  const chartData = useMemo(() => {
    if (!model?.months?.length) return [];
    return model.months.map((m) => ({
      month: m,
      rm: cm.val(model.monthMap[m].rm),
      nu: cm.val(model.monthMap[m].nu),
    }));
  }, [model, cm]);

  const winnerOf = (m) => {
    const a = Number(model.rm[m.key] || 0), b = Number(model.nu[m.key] || 0);
    if (a === b) return null;
    const rmWins = m.higher ? a > b : a < b;
    return rmWins ? 'rm' : 'nu';
  };

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 border border-blue-100 shadow-sm rounded-xl">
            <img src="/6033716.png" alt="Meta Ads" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Remarketing vs New User</h1>
            <p className="text-sm text-gray-500 mt-1">Live Meta data · campaigns with "{keyword}" in the name = Remarketing, others = New User</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <AccountSwitcher platform="META_ADS" />
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">RM keyword</label>
            <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
              className="w-20 px-2 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 outline-none" />
          </div>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none hover:border-gray-300">
            {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-xl text-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {loading && !resp && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Fetching live monthly data…
        </div>
      )}

      {resp && (
        <>
          {/* Segment summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SegmentCard title="Remarketing" sub={`${model.rmCount} campaigns · "${keyword}"`} color={RM_COLOR} icon={Repeat} seg={model.rm} />
            <SegmentCard title="New User" sub={`${model.nuCount} campaigns`} color={NU_COLOR} icon={Users} seg={model.nu} />
          </div>

          {/* Comparison table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Metric</th>
                  <th className="text-right px-4 py-3 font-semibold" style={{ color: RM_COLOR }}>Remarketing</th>
                  <th className="text-right px-4 py-3 font-semibold" style={{ color: NU_COLOR }}>New User</th>
                  <th className="text-right px-4 py-3 font-semibold">Diff (NU−RM)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {METRICS.map((m) => {
                  const a = Number(model.rm[m.key] || 0), b = Number(model.nu[m.key] || 0);
                  const diff = b - a;
                  const diffPct = a !== 0 ? (diff / a) * 100 : null;
                  const w = winnerOf(m);
                  const goodForNU = diff !== 0 && (m.higher ? diff > 0 : diff < 0);
                  return (
                    <tr key={m.key} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-600">{m.label}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${w === 'rm' ? 'font-bold' : 'text-gray-800'}`} style={w === 'rm' ? { color: RM_COLOR } : {}}>{m.fmt(a)}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${w === 'nu' ? 'font-bold' : 'text-gray-800'}`} style={w === 'nu' ? { color: NU_COLOR } : {}}>{m.fmt(b)}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${diff === 0 ? 'text-gray-400' : goodForNU ? 'text-emerald-600' : 'text-red-600'}`}>
                        {diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${m.fmt(diff)}${diffPct !== null ? ` (${diffPct > 0 ? '+' : ''}${fmtNum(diffPct, 1)}%)` : ''}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Monthly breakdown chart */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-gray-700">Monthly breakdown</h3>
              <div className="flex gap-1">
                {CHART_METRICS.map((m) => (
                  <button key={m.key} onClick={() => setChartMetric(m.key)}
                    className={`px-2.5 py-1 text-xs rounded-lg border ${chartMetric === m.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsLineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => cm.fmt(v)} width={70} />
                <Tooltip formatter={(v, n) => [cm.fmt(v), n === 'rm' ? 'Remarketing' : 'New User']} />
                <Legend formatter={(v) => (v === 'rm' ? 'Remarketing' : 'New User')} />
                <Line type="monotone" dataKey="rm" stroke={RM_COLOR} strokeWidth={2} dot={{ r: 2 }} name="rm" />
                <Line type="monotone" dataKey="nu" stroke={NU_COLOR} strokeWidth={2} dot={{ r: 2 }} name="nu" />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 text-sm font-semibold text-gray-700">Monthly breakdown — Remarketing vs New User</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-400 uppercase border-b border-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold" rowSpan={2}>Month</th>
                    <th className="text-center px-3 py-1.5 font-semibold border-l border-gray-100" colSpan={3} style={{ color: RM_COLOR }}>Remarketing</th>
                    <th className="text-center px-3 py-1.5 font-semibold border-l border-gray-100" colSpan={3} style={{ color: NU_COLOR }}>New User</th>
                  </tr>
                  <tr>
                    <th className="text-right px-3 py-1.5 font-medium border-l border-gray-100">Spend</th>
                    <th className="text-right px-3 py-1.5 font-medium">Revenue</th>
                    <th className="text-right px-3 py-1.5 font-medium">ROAS</th>
                    <th className="text-right px-3 py-1.5 font-medium border-l border-gray-100">Spend</th>
                    <th className="text-right px-3 py-1.5 font-medium">Revenue</th>
                    <th className="text-right px-3 py-1.5 font-medium">ROAS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {model.months.map((m) => {
                    const rm = model.monthMap[m].rm, nu = model.monthMap[m].nu;
                    const rRm = rm.spend > 0 ? rm.revenue / rm.spend : 0;
                    const rNu = nu.spend > 0 ? nu.revenue / nu.spend : 0;
                    return (
                      <tr key={m} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-700">{m}</td>
                        <td className="px-3 py-2 text-right tabular-nums border-l border-gray-100">{fmtMoney(rm.spend)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(rm.revenue)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium" style={{ color: RM_COLOR }}>{rm.spend > 0 ? roasStr(rRm) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums border-l border-gray-100">{fmtMoney(nu.spend)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(nu.revenue)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium" style={{ color: NU_COLOR }}>{nu.spend > 0 ? roasStr(rNu) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            Source: Meta Graph API (live, monthly) · Account {resp.accountId} · {resp.window?.since} → {resp.window?.until}
          </p>
        </>
      )}
    </div>
  );
}

function SegmentCard({ title, sub, color, icon: Icon, seg }) {
  const kpis = [
    ['Spend', fmtMoney(seg.spend)],
    ['Revenue', fmtMoney(seg.revenue)],
    ['ROAS', seg.spend > 0 ? roasStr(seg.roas) : '—'],
    ['Conversions', fmtInt(seg.conversions)],
    ['CPA', seg.conversions > 0 ? fmtMoney(seg.cpa) : '—'],
    ['CTR', fmtPct(seg.ctr)],
  ];
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="p-2 rounded-lg" style={{ backgroundColor: color + '15' }}><Icon className="w-4 h-4" style={{ color }} /></span>
        <div>
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          <p className="text-[11px] text-gray-400">{sub}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {kpis.map(([l, v]) => (
          <div key={l} className="rounded-lg bg-gray-50 p-2.5">
            <div className="text-[10px] text-gray-400 uppercase">{l}</div>
            <div className="text-sm font-bold text-gray-900 tabular-nums">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
